import React, { useState, useEffect } from "react";
import { Menu } from "antd";
import {
  SnippetsOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  FileOutlined,
  UserAddOutlined,
  ClusterOutlined,
  ScheduleOutlined,
  BookOutlined,
  IdcardOutlined,
  ControlOutlined,
  LogoutOutlined,
  WalletOutlined,
  BlockOutlined,
  SafetyOutlined,
  CreditCardOutlined,
  MessageOutlined,
  MailOutlined,
  LockOutlined,
  ApartmentOutlined,
  HomeOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  TagOutlined,
  ToolOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
  SkinOutlined,
  CheckCircleOutlined,
  TrademarkCircleOutlined,
  DollarCircleOutlined,
  HeartOutlined,
  ColumnWidthOutlined,
  StarOutlined,
  SmileOutlined,
  ContactsOutlined,
  UsergroupAddOutlined,
  CarryOutOutlined,
  SecurityScanOutlined,
  CheckSquareOutlined,
  FormOutlined,
  WhatsAppOutlined,
  UnorderedListOutlined,
  GlobalOutlined,
  ReadOutlined,
  AccountBookOutlined,
  DollarOutlined,
  SolutionOutlined,
  // CreditCardOutlined,
} from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { Module } from "../../types/Module";
import { buildMenuTree } from "./menuUtils";

type Props = {
  modules: Module[];
  selectedKey: string;
  openKeys: string[];
  darkMode: boolean;
  themeStyles: {
    headerBg: string;
    siderBg: string;
    footerBg: string;
  };
};
const iconMap: Record<string, React.ReactElement> = {
  dashboard: <DesktopOutlined />,
  tickets: <SnippetsOutlined />,
  usermaster: <UserOutlined />,
  frontdesk: <UserOutlined />,
  user: <UserAddOutlined />,
  master: <AppstoreOutlined />,
  student: <SmileOutlined />,
  team: <TeamOutlined />,
  file: <FileOutlined />,
  setting: <SettingOutlined />,
  category: <ClusterOutlined />,
  class: <ScheduleOutlined />,
  subject: <BookOutlined />,
  lead: <ContactsOutlined />,
  module: <BlockOutlined />,
  modulemaster: <ControlOutlined />,
  logout: <LogoutOutlined />,
  feesstructure: <WalletOutlined />,
  permission: <SafetyOutlined />,
  paymentsetting: <CreditCardOutlined />,
  smssetting: <MessageOutlined />,
  emailsetting: <MailOutlined />,
  changepassword: <LockOutlined />,
  organizationmaster: <ApartmentOutlined />,
  organization: <HomeOutlined />,
  exams: <FileTextOutlined />,
  timetable: <CalendarOutlined />,
  productmaster: <ShoppingOutlined />,
  products: <TagOutlined />,
  servicemaster: <ToolOutlined />,
  services: <CustomerServiceOutlined />,
  sessionmaster: <ClockCircleOutlined />,
  templatemaster: <FileTextOutlined />,
  fabrictype: <SkinOutlined />,
  qualitynames: <CheckCircleOutlined />,
  brandnames: <TrademarkCircleOutlined />,
  saletypes: <DollarCircleOutlined />,
  productuse: <ToolOutlined />,
  caring: <HeartOutlined />,
  sizes: <ColumnWidthOutlined />,
  productgrade: <StarOutlined />,
  studentleads: <UsergroupAddOutlined />,
  attendancemaster: <FormOutlined />,
  attendance: <CheckSquareOutlined />,
  gatepass: <SecurityScanOutlined />,
  holidays: <CarryOutOutlined />,
  whatsapp: <WhatsAppOutlined />,
  result: <UnorderedListOutlined  />,
  webmaster: <GlobalOutlined  />,
  booknumber: <ReadOutlined  />,
  billing: <AccountBookOutlined  />,
  voucher: <DollarOutlined  />,
  clientmaster: <SolutionOutlined  />,
  studentmaster: <UserOutlined  />,


};

const getIcon = (iconName: string): React.ReactElement | null => {
  return iconMap[iconName.toLowerCase()] ?? null;
};

const buildMenuItems = (modules: Module[]): any[] =>
  modules.map((mod) => {
    const icon = getIcon(mod.module_icon || mod.module_name);
    // console.log("icon", icon)
    if (mod.children && mod.children.length > 0) {
      return {
        key: String(mod.module_id),
        icon,
        label: mod.module_name,
        children: buildMenuItems(mod.children), // recursion
      };
    }

    return {
      key: String(mod.module_id),
      icon,
      label: <Link to={mod.module_url}>{mod.module_name}</Link>,
    };
  });

const SidebarMenu: React.FC<Props> = ({ modules, selectedKey, openKeys, darkMode, themeStyles }) => {
  const { pathname } = useLocation();
  const [activeKey, setActiveKey] = useState<string>(selectedKey);
  const [openKeysState, setOpenKeysState] = useState<string[]>(openKeys);

  useEffect(() => {
    const findActiveKey = (modules: Module[], currentPath: string): string | null => {
      for (const module of modules) {
        if (module.module_url === currentPath) {
          return String(module.module_id);
        }
        if (module.children) {
          const found = findActiveKey(module.children, currentPath);
          if (found) return found;
        }
      }
      return null;
    };

    const activeModuleKey = findActiveKey(modules, pathname);
    if (activeModuleKey) {
      setActiveKey(activeModuleKey);
    }
  }, [pathname, modules]);

  useEffect(() => {
    const findOpenKeys = (modules: Module[], currentPath: string): string[] => {
      const openKeysArray: string[] = [];

      for (const module of modules) {
        if (
          module.module_url === currentPath ||
          (module.children && module.children.some((child) => child.module_url === currentPath))
        ) {
          openKeysArray.push(String(module.module_id));
        }

        if (module.children) {
          openKeysArray.push(...findOpenKeys(module.children, currentPath));
        }
      }

      return openKeysArray;
    };

    const newOpenKeys = findOpenKeys(modules, pathname);
    setOpenKeysState(newOpenKeys);
  }, [pathname, modules]);

  const treeData = buildMenuTree(modules);
  const menuItems = buildMenuItems(treeData);

  const onOpenChange = (keys: string[]) => {
    setOpenKeysState(keys);
  };

  return (
    <Menu
      theme={darkMode ? 'light' : 'dark'}
      mode="inline"
      selectedKeys={[activeKey]}
      openKeys={openKeysState}
      onOpenChange={onOpenChange}
      items={menuItems} // ✅ using items instead of children
      style={{
        backgroundColor: themeStyles.siderBg,
      }}
    />
  );
};

export default SidebarMenu;
